import React from "react";

/** Ancho útil carta vertical para html2pdf (debe coincidir con presupuestoPdfUtils). */
const PRESUPUESTO_PDF_WIDTH_MM = 190;

function VistaPreviaPresupuestoPDF({ presupuesto, client, paciente, encabezadoPersonalizado, usuario }) {

    const formatearFecha = (fecha) => {
        if (!fecha) return '';
        const date = new Date(fecha);
        const dia = String(date.getDate()).padStart(2, '0');
        const mes = String(date.getMonth() + 1).padStart(2, '0');
        const anio = String(date.getFullYear()).slice(-2);
        return `${dia}/${mes}/${anio}`;
    };

    const construirDireccionCompleta = () => {
        if (!paciente) return '';
        const partes = [];
        if (paciente.direccion) partes.push(paciente.direccion);
        if (paciente.zona) partes.push(paciente.zona);
        if (paciente.nombre_ciudad_residencia) partes.push(paciente.nombre_ciudad_residencia);
        if (paciente.nombre_departamento_residencia) partes.push(paciente.nombre_departamento_residencia);
        if (paciente.nombre_pais_residencia) partes.push(paciente.nombre_pais_residencia);
        return partes.join(', ') || paciente.direccion || '';
    };

    const obtenerDocumento = () => {
        if (!paciente) return '';
        return paciente.numero_documento || paciente.numeroDocumento || '';
    };

    const mostrarDescripcion = presupuesto.detalle?.some(item => String(item.descripcion || '').trim() !== '');
    const colspanTotales = mostrarDescripcion ? 7 : 6;

    const totalAjuste = presupuesto.detalle?.reduce((acc, item) => {
        return acc + (parseFloat(item.ajuste) || 0);
    }, 0) || 0;

    const limpiarHTML = (html) => {
        if (!html) return '';
        return html
            .replace(/<p>/gi, '<div style="margin: 0; padding: 0; line-height: 1.1;">')
            .replace(/<\/p>/gi, '</div>')
            .replace(/<br\s*\/?>/gi, '<br style="line-height: 1.1;" />');
    };

    const generarEncabezadoHTML = () => {
        if (!encabezadoPersonalizado) {
            return `
                <table style="width: 100%; border-collapse: collapse; margin-bottom: 10px;">
                    <tr>
                        <td style="width: 25%; text-align: center; vertical-align: top;">
                            ${client?.logo ? `<img src="${client.logo}" style="max-width: 120px; max-height: 70px;" />` : ''}
                        </td>
                        <td style="width: 50%; text-align: center; vertical-align: top;">
                            <div style="font-size: 13px; font-weight: bold; margin-bottom: 4px;">${client?.nombre || ''}</div>
                            <div style="font-size: 9px; line-height: 1.3;">
                                ${client?.direccion || ''}<br />
                                ${client?.telefono || ''}<br />
                                Email: ${client?.email || ''}
                            </div>
                        </td>
                        <td style="width: 25%;"></td>
                    </tr>
                </table>
            `;
        }

        return `
            <table style="width: 100%; border-collapse: collapse; margin-bottom: 10px; background-color: #ffffff;">
                <tr>
                    <td style="width: 30%; padding: 2px; font-size: 9px; line-height: 1.1; vertical-align: top; word-wrap: break-word;">
                        ${limpiarHTML(encabezadoPersonalizado.izquierda)}
                    </td>
                    <td style="width: 40%; padding: 2px; text-align: center; font-size: 10px; line-height: 1.1; vertical-align: top; word-wrap: break-word;">
                        ${limpiarHTML(encabezadoPersonalizado.centro)}
                    </td>
                    <td style="width: 30%; padding: 2px; text-align: right; font-size: 9px; line-height: 1.1; vertical-align: top; word-wrap: break-word;">
                        ${limpiarHTML(encabezadoPersonalizado.derecha)}
                    </td>
                </tr>
            </table>
        `;
    };

    const nombrePaciente = paciente ? `${paciente.primer_nombre || ''} ${paciente.segundo_nombre || ''} ${paciente.primer_apellido || ''} ${paciente.segundo_apellido || ''}`.trim() : 'N/A';
    const nombreUsuario = usuario ? usuario.name : 'N/A';

    const cellBorder = { border: '1px solid #dee2e6', padding: '4px 3px' };
    const thStyle = { ...cellBorder, fontSize: '8px', fontWeight: 'bold', backgroundColor: '#e9ecef', verticalAlign: 'middle' };
    const tdStyle = { ...cellBorder, fontSize: '8px', verticalAlign: 'top', wordBreak: 'break-word' };
    const tdNumStyle = { ...tdStyle, textAlign: 'right', whiteSpace: 'nowrap' };
    const tdCenterStyle = { ...tdStyle, textAlign: 'center' };

    const rootStyle = {
        width: `${PRESUPUESTO_PDF_WIDTH_MM}mm`,
        maxWidth: `${PRESUPUESTO_PDF_WIDTH_MM}mm`,
        boxSizing: 'border-box',
        padding: '6px',
        fontFamily: 'Arial, sans-serif',
        backgroundColor: '#ffffff',
        fontSize: '10px',
        overflow: 'hidden',
    };

    return (
        <div id="presupuesto-pdf-editable" style={rootStyle}>
            <div dangerouslySetInnerHTML={{ __html: generarEncabezadoHTML() }} />

            <div style={{ marginBottom: '8px' }}>
                <div style={{ backgroundColor: '#e9ecef', padding: '6px 8px', fontWeight: 'bold', fontSize: '11px', borderRadius: '3px', textAlign: 'center' }}>
                    PRESUPUESTO N° {presupuesto.numero_presupuesto}
                </div>
            </div>

            <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '10px', fontSize: '9px', tableLayout: 'fixed' }}>
                <tbody>
                    <tr>
                        <td style={{ ...cellBorder, backgroundColor: '#e9ecef', fontWeight: 'bold', width: '14%' }}>FECHA CREACIÓN</td>
                        <td style={{ ...cellBorder, backgroundColor: '#ffffff', width: '19%' }}>{formatearFecha(presupuesto.fecha_creacion)}</td>
                        <td style={{ ...cellBorder, backgroundColor: '#e9ecef', fontWeight: 'bold', width: '14%' }}>VÁLIDO HASTA</td>
                        <td style={{ ...cellBorder, backgroundColor: '#ffffff', width: '19%' }}>{formatearFecha(presupuesto.fecha_vence)}</td>
                        <td style={{ ...cellBorder, backgroundColor: '#e9ecef', fontWeight: 'bold', width: '14%' }}>FORMA DE PAGO</td>
                        <td style={{ ...cellBorder, backgroundColor: '#ffffff', width: '20%' }}>{presupuesto.forma_pago}</td>
                    </tr>
                    <tr>
                        <td style={{ ...cellBorder, backgroundColor: '#e9ecef', fontWeight: 'bold' }}>CREADO POR</td>
                        <td colSpan="5" style={{ ...cellBorder, backgroundColor: '#ffffff' }}>{nombreUsuario}</td>
                    </tr>
                </tbody>
            </table>

            <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '12px', fontSize: '9px', tableLayout: 'fixed' }}>
                <tbody>
                    <tr>
                        <td style={{ ...cellBorder, backgroundColor: '#e9ecef', fontWeight: 'bold', width: '14%' }}>PACIENTE</td>
                        <td style={{ ...cellBorder, backgroundColor: '#ffffff', width: '36%' }}>{nombrePaciente}</td>
                        <td style={{ ...cellBorder, backgroundColor: '#e9ecef', fontWeight: 'bold', width: '14%' }}>DOCUMENTO</td>
                        <td style={{ ...cellBorder, backgroundColor: '#ffffff', width: '36%' }}>{obtenerDocumento()}</td>
                    </tr>
                    <tr>
                        <td style={{ ...cellBorder, backgroundColor: '#e9ecef', fontWeight: 'bold' }}>TELÉFONO</td>
                        <td style={{ ...cellBorder, backgroundColor: '#ffffff' }}>{paciente?.telefono || ''}</td>
                        <td style={{ ...cellBorder, backgroundColor: '#e9ecef', fontWeight: 'bold' }}>CELULAR</td>
                        <td style={{ ...cellBorder, backgroundColor: '#ffffff' }}>{paciente?.celular || ''}</td>
                    </tr>
                    <tr>
                        <td style={{ ...cellBorder, backgroundColor: '#e9ecef', fontWeight: 'bold' }}>EMAIL</td>
                        <td style={{ ...cellBorder, backgroundColor: '#ffffff', wordBreak: 'break-all' }}>{paciente?.email || ''}</td>
                        <td style={{ ...cellBorder, backgroundColor: '#e9ecef', fontWeight: 'bold' }}>DIRECCIÓN</td>
                        <td style={{ ...cellBorder, backgroundColor: '#ffffff', wordBreak: 'break-word' }}>{construirDireccionCompleta()}</td>
                    </tr>
                </tbody>
            </table>

            <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '12px', tableLayout: 'fixed' }}>
                <colgroup>
                    <col style={{ width: mostrarDescripcion ? '7%' : '8%' }} />
                    <col style={{ width: mostrarDescripcion ? '18%' : '22%' }} />
                    {mostrarDescripcion && <col style={{ width: '12%' }} />}
                    <col style={{ width: '7%' }} />
                    <col style={{ width: '13%' }} />
                    <col style={{ width: '9%' }} />
                    <col style={{ width: '9%' }} />
                    <col style={{ width: '10%' }} />
                    <col style={{ width: '15%' }} />
                </colgroup>
                <thead>
                    <tr style={{ backgroundColor: '#e9ecef' }}>
                        <th style={thStyle}>Código</th>
                        <th style={thStyle}>Servicio</th>
                        {mostrarDescripcion && <th style={thStyle}>Descripción</th>}
                        <th style={{ ...thStyle, textAlign: 'center' }}>Cant.</th>
                        <th style={{ ...thStyle, textAlign: 'right' }}>Precio Unit.</th>
                        <th style={{ ...thStyle, textAlign: 'center' }}>Desc.</th>
                        <th style={{ ...thStyle, textAlign: 'center' }}>Imp.</th>
                        <th style={{ ...thStyle, textAlign: 'right' }}>Ajuste</th>
                        <th style={{ ...thStyle, textAlign: 'right' }}>Total</th>
                    </tr>
                </thead>
                <tbody>
                    {presupuesto.detalle?.map((item, index) => (
                        <tr key={index} style={{ backgroundColor: index % 2 === 0 ? '#f8f9fa' : '#ffffff' }}>
                            <td style={tdStyle}>{item.lista_precio?.codigo}</td>
                            <td style={tdStyle}>{item.servicio || item.lista_precio?.nombre}</td>
                            {mostrarDescripcion && (
                                <td style={tdStyle}>{item.descripcion || ''}</td>
                            )}
                            <td style={tdCenterStyle}>{item.cantidad}</td>
                            <td style={tdNumStyle}>${Math.round(parseFloat(item.precio_unitario)).toLocaleString('es-CO')}</td>
                            <td style={tdCenterStyle}>{item.porcentaje}%</td>
                            <td style={tdCenterStyle}>{item.impuesto}%</td>
                            <td style={tdNumStyle}>${Math.round(parseFloat(item.ajuste) || 0).toLocaleString('es-CO')}</td>
                            <td style={{ ...tdNumStyle, fontWeight: 'bold' }}>${Math.round(parseFloat(item.total)).toLocaleString('es-CO')}</td>
                        </tr>
                    ))}
                </tbody>
                <tfoot>
                    <tr>
                        <td colSpan={colspanTotales} style={{ border: 'none' }}></td>
                        <td style={{ ...cellBorder, textAlign: 'left', backgroundColor: '#e9ecef', fontWeight: 'bold', fontSize: '8px' }}>Subtotal:</td>
                        <td style={{ ...cellBorder, textAlign: 'right', backgroundColor: '#e9ecef', fontSize: '8px' }}>${Math.round(parseFloat(presupuesto.valor_bruto)).toLocaleString('es-CO')}</td>
                    </tr>
                    <tr>
                        <td colSpan={colspanTotales} style={{ border: 'none' }}></td>
                        <td style={{ ...cellBorder, textAlign: 'left', backgroundColor: '#e9ecef', fontWeight: 'bold', color: '#dc3545', fontSize: '8px' }}>Descuento:</td>
                        <td style={{ ...cellBorder, textAlign: 'right', backgroundColor: '#e9ecef', color: '#dc3545', fontSize: '8px' }}>
                            -${Math.round(presupuesto.detalle?.reduce((acc, item) => {
                                const precioBase = parseFloat(item.precio_unitario) * parseFloat(item.cantidad);
                                return acc + (precioBase * (parseFloat(item.porcentaje) / 100));
                            }, 0) || 0).toLocaleString('es-CO')}
                        </td>
                    </tr>
                    <tr>
                        <td colSpan={colspanTotales} style={{ border: 'none' }}></td>
                        <td style={{ ...cellBorder, textAlign: 'left', backgroundColor: '#e9ecef', fontWeight: 'bold', fontSize: '8px' }}>Impuesto:</td>
                        <td style={{ ...cellBorder, textAlign: 'right', backgroundColor: '#e9ecef', fontSize: '8px' }}>
                            ${Math.round(presupuesto.detalle?.reduce((acc, item) => {
                                const precioBase = parseFloat(item.precio_unitario) * parseFloat(item.cantidad);
                                const descuento = precioBase * (parseFloat(item.porcentaje) / 100);
                                const baseConDescuento = precioBase - descuento;
                                return acc + (baseConDescuento * (parseFloat(item.impuesto) / 100));
                            }, 0) || 0).toLocaleString('es-CO')}
                        </td>
                    </tr>
                    <tr>
                        <td colSpan={colspanTotales} style={{ border: 'none' }}></td>
                        <td style={{ ...cellBorder, textAlign: 'left', backgroundColor: '#e9ecef', fontWeight: 'bold', fontSize: '8px' }}>Ajuste:</td>
                        <td style={{ ...cellBorder, textAlign: 'right', backgroundColor: '#e9ecef', fontSize: '8px' }}>
                            ${Math.round(totalAjuste).toLocaleString('es-CO')}
                        </td>
                    </tr>
                    <tr>
                        <td colSpan={colspanTotales} style={{ border: 'none' }}></td>
                        <td style={{ ...cellBorder, textAlign: 'left', backgroundColor: '#e9ecef', fontWeight: 'bold', fontSize: '9px' }}>Total:</td>
                        <td style={{ ...cellBorder, textAlign: 'right', backgroundColor: '#e9ecef', fontSize: '9px', fontWeight: 'bold' }}>${Math.round(parseFloat(presupuesto.valor_total)).toLocaleString('es-CO')}</td>
                    </tr>
                </tfoot>
            </table>

            {presupuesto.observaciones && (
                <div style={{ marginTop: '12px' }}>
                    <div style={{ fontSize: '10px', fontWeight: 'bold', marginBottom: '4px' }}>Observaciones:</div>
                    <p style={{ fontSize: '9px', margin: 0 }}>{presupuesto.observaciones}</p>
                </div>
            )}

            <div style={{ marginTop: '16px', padding: '8px', backgroundColor: '#d1ecf1', border: '1px solid #bee5eb', borderRadius: '4px' }}>
                <p style={{ margin: 0, fontSize: '9px', color: '#0c5460' }}>
                    <strong>Nota:</strong> Este es un presupuesto válido hasta la fecha indicada. Los precios pueden estar sujetos a cambios.
                </p>
            </div>
        </div>
    );
}

export default VistaPreviaPresupuestoPDF;
